'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { Card } from '@/components/ui/card';
import { Pill } from '@/components/ui/pill';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';
import { Rocket, Lightbulb, TrendingUp, Globe } from 'lucide-react';

export function OneStopSolutionSection() {
    const t = useTranslations('aboutPage.oneStopSolution');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const goals = [
        {
            icon: Rocket,
            description: t('goal1'),
        },
        {
            icon: Lightbulb,
            description: t('goal2'),
        },
        {
            icon: TrendingUp,
            description: t('goal3'),
        },
        {
            icon: Globe,
            description: t('goal4'),
        },
    ];

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="default"
            padding="md"
            className="relative overflow-hidden bg-white dark:bg-gray-950"
        >
            {/* Decorative background */}
            <div className="pointer-events-none absolute inset-0 opacity-40">
                <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
                <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className="container relative z-10 max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Left Column - Text Content */}
                    <div className={cn(
                        "transition-all duration-700",
                        sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                    )}>
                        {/* Pill */}
                        <div className="mb-4">
                            <Pill variant="left" className="text-gray-900 dark:text-white">
                                {t('pill')}
                            </Pill>
                        </div>

                        {/* Title */}
                        <Typography variant="h2" className="mb-6 text-gray-900 dark:text-white font-bold text-3xl md:text-4xl leading-tight text-left">
                            {t('title')}
                        </Typography>

                        {/* Description */}
                        <Typography variant="p" className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed text-left">
                            {t('description')}
                        </Typography>
                    </div>

                    {/* Right Column - Goals Grid */}
                    <div className={cn(
                        "transition-all duration-700",
                        sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                    )} style={{ transitionDelay: '200ms' }}>
                        {/* Goals Title */}
                        <Typography variant="h3" className="mb-6 text-gray-900 dark:text-white font-semibold text-xl md:text-2xl text-left">
                            {t('goalsTitle')}
                        </Typography>

                        {/* 2x2 Grid of Goal Cards */}
                        <div className="grid grid-cols-2 gap-6">
                            {goals.map((goal, index) => {
                                const IconComponent = goal.icon;
                                return (
                                    <Card
                                        key={index}
                                        title=""
                                        description={goal.description}
                                        icon={IconComponent}
                                        showFooterLink={false}
                                        className="bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30"
                                        animationDelay={300 + index * 100}
                                        renderContent={
                                            <div className="flex flex-col">
                                                <div className="mb-4">
                                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-500/10 dark:bg-primary-500/20">
                                                        <IconComponent className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                                                    </div>
                                                </div>
                                                <Typography variant="p" className="text-gray-900 dark:text-white text-sm md:text-base leading-relaxed">
                                                    {goal.description}
                                                </Typography>
                                            </div>
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

